import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { isNotNullish } from '@payaca/utilities/guards';
import React, {
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import Button, { ButtonProps } from '../Button/Button';
import FilterDropdown from '../Dropdown/FilterDropdown';
import Input, { InputIcon } from '../Input/Input';
import { SelectOption, ValueType } from '../Select/Select';
import FloatingTooltip, {
  IProps as IFloatingTooltipProps,
} from '../Tooltip/FloatingTooltip';
import TooltipCore from '../Tooltip/TooltipCore';
import MultiFilterSidebar, {
  IProps as MultiFilterSidebarProps,
} from './components/MultiFilterSidebar';
import NumberOfSelectedFilters from './components/NumberOfSelectedFilters';
import { PaginationBar as BasePaginationBar } from './components/PaginationBar';
import StaticGlobalActionBar from './components/StaticGlobalActionBar';
import { Table } from './components/Table';

const ManageableItemsList_: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return (
    <div
      className={clstx(
        'typography:prose overflow-hidden rounded-lg border border-gray-200 bg-white',
        className
      )}
    >
      {children}
    </div>
  );
};

export type HeaderBarProps = {
  heading?: string;
  subHeading?: string | ReactNode;
  buttons?: ReactNode;
};
const HeaderBar: FC<HeaderBarProps> = ({ heading, subHeading, buttons }) => {
  if (!heading && !subHeading && !buttons) return null;

  return (
    <div className="typography:prose grid gap-6 border-b border-gray-200 p-4 pl-6 md:flex md:items-center md:justify-between">
      <div>
        {heading && <h3>{heading}</h3>}
        {subHeading &&
          (typeof subHeading === 'string' ? <p>{subHeading}</p> : subHeading)}
      </div>

      {buttons && (
        <div className="shrink-0">
          <div className="inline-flex items-center gap-x-2">{buttons}</div>
        </div>
      )}
    </div>
  );
};

const ActionBar: FC<PropsWithChildren<{ className?: string }>> = ({
  className,
  children,
}) => {
  return (
    <div
      className={clstx(
        'flex flex-wrap items-center justify-end gap-2 border-b border-gray-200 px-4 py-3 empty:hidden',
        className
      )}
    >
      {children}
    </div>
  );
};

const SearchInput: FC<ComponentProps<typeof Input>> = ({
  className,
  ...rest
}) => {
  return (
    <Input
      // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Relative to the parent font size
      className={clstx('mr-auto w-full md:w-[35em] shrink', className)}
      type="search"
      aria-label={`Search`}
      leadingElement={<InputIcon name="search-lg.3" />}
      sizeVariant="sm"
      placeholder="Search"
      {...rest}
    />
  );
};

const BasicFilter = <
  TValueType extends ValueType,
  TMetaDataType = Record<string, any>,
>({
  options,
  value,
  onChange,
  name,
}: {
  options: SelectOption<TValueType, TMetaDataType>[];
  value: TValueType[];
  onChange: (value: TValueType[]) => void;
  name?: string;
}) => {
  return (
    <FilterDropdown
      name={name}
      variant={'secondary'}
      size={'sm'}
      filters={options.map((option) => ({
        label: option.label,
        selected: value.includes(option.value),
        onClick: () => {
          const isCurrentlySelected = value.includes(option.value);

          return isCurrentlySelected
            ? // Remove the option from the selected values
              onChange((value || []).filter((i) => i !== option.value))
            : // Add the option to the selected values
              onChange([...value, option.value]);
        },
      }))}
    />
  );
};

const AdvancedFilter: FC<
  Pick<MultiFilterSidebarProps, 'options' | 'appliedFilters' | 'onApplyFilters'>
> = ({ appliedFilters, options, onApplyFilters }) => {
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const numberOfSelectedFilters = useMemo(() => {
    return appliedFilters
      ? Object.values(appliedFilters).filter((f: any) =>
          isNotNullish(f) ? f.length : false
        ).length
      : 0;
  }, [appliedFilters]);

  return (
    <>
      <Button
        onClick={() => setShowFilterSidebar(true)}
        variant="secondary"
        size={'sm'}
      >
        <UntitledIcon name="sliders-01.3" className="h-3.5 w-3.5 -rotate-90" />
        Filter
        {!!numberOfSelectedFilters && (
          <NumberOfSelectedFilters
            numberOfSelectedFilters={numberOfSelectedFilters}
          />
        )}
      </Button>
      <MultiFilterSidebar
        isOpen={showFilterSidebar}
        onClose={() => setShowFilterSidebar(false)}
        options={options}
        appliedFilters={appliedFilters}
        onApplyFilters={onApplyFilters}
      />
    </>
  );
};

type TGlobalActionProps = ButtonProps &
  Omit<IFloatingTooltipProps, 'showArrow' | 'children'> & {
    tooltipContent: React.ReactNode;
  };

const GlobalAction: FC<TGlobalActionProps> = (props) => {
  const {
    placement,
    arrowClassName,
    enabled,
    tooltipContent,
    children,
    size = 'sm',
    ...rest
  } = props;

  return (
    <FloatingTooltip
      placement={placement}
      arrowClassName={arrowClassName}
      enabled={enabled}
      showArrow
    >
      <Button size={size} {...rest}>
        {children}
      </Button>

      <TooltipCore>{tooltipContent}</TooltipCore>
    </FloatingTooltip>
  );
};

const PaginationBar: FC<
  | {
      type: 'custom';
      pageSize: number;
      currentPage: number;
      totalItems: number;
      onPageChange: (page: number) => void;
    }
  | {
      type: 'from-paginated-result';
      limit?: number;
      offset?: number;
      totalCount?: number;
      onPageChange: (page: number) => void;
    }
> = (props) => {
  const className = 'border-t border-gray-200 px-6 py-4';

  if (props.type === 'from-paginated-result') {
    const {
      limit = PAGINATION_ARG_DEFAULTS.limit,
      offset = PAGINATION_ARG_DEFAULTS.offset,
      totalCount = 0,
    } = props;

    return (
      <BasePaginationBar
        pageSize={limit}
        currentPage={limit > 0 ? Math.ceil(offset / limit) + 1 : 1}
        totalItems={totalCount || 0}
        onPageChange={props.onPageChange}
        className={className}
      />
    );
  }

  const { pageSize, currentPage, totalItems } = props;

  return (
    <BasePaginationBar
      pageSize={pageSize}
      currentPage={currentPage}
      totalItems={totalItems}
      onPageChange={props.onPageChange}
      className={className}
    />
  );
};

const InfinitePaginationBar: FC<{
  className?: string;
  count: number;
  totalCount: number;
  onLoadMore: () => void;
  hasNextPage: boolean;
}> = (props) => {
  const { className, count, totalCount, onLoadMore, hasNextPage } = props;

  return (
    <div
      className={clstx(
        'typography:prose grid gap-3 md:flex md:items-center md:justify-between border-t border-gray-200 px-6 py-4',
        className
      )}
    >
      <p>
        Showing {count} of {totalCount}
      </p>
      <div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onLoadMore}
          disabled={!hasNextPage}
        >
          Load more
        </Button>
      </div>
    </div>
  );
};

export const ManageableItemsList = Object.assign(ManageableItemsList_, {
  HeaderBar,
  ActionBar: Object.assign(ActionBar, {
    SearchInput,
    BasicFilter,
    AdvancedFilter,
    GlobalAction,
  }),
  PaginationBar,
  InfinitePaginationBar,
  Table,
  StaticGlobalActionBar,
});

export default ManageableItemsList;
